import { useCampaignContext } from 'context/CampaignContext';
import {
  metric,
  detailedDimensionsDetail,
  calculatedMetricsDetail,
  metricFunction,
  queryInput,
  MetricsResponse,
  GridLayout,
  BenchmarkingIndexValues,
} from 'API';
import { CCData, useDataQuery } from 'customHooks/useDataQuery';
import { ChangeEvent, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { platformLogoSelector } from 'methods/platformLogoSelector';
import { DarkButton } from 'components/style/Global';
import { prepQuery, prepQueryCC } from 'methods/prepQuery';
import AuthContext from 'context/AuthContext';
import { TxtContainer } from 'components/Main/Main.styles';
import ReactLoading from 'react-loading';
import { useTheme } from 'styled-components';
import { DetailsPane, KPIResultsContainer, KPIRowPickerItem, OverwriteMainContainer, SingleColumn, StyledLabel } from './CustomReportQuery.Styles';
import { useCustomReportContext } from 'context/CustomReportContext';
import { useJoinData } from 'customHooks/useJoinData';
import { updateCustomSource } from 'methods/updateCustomSource';
import CustomReportTable from './CustomReportTable';
import { useSaveCustomReportQuery } from './CustomReportsHooks/useCustomReportsQueries';
import { queryValues } from './CustomReportSection';
import { removeTypename } from 'methods/removeTypename';
import { useGetCustomReportPublishedData, useSaveNewSectionItem } from './CustomReportsHooks/useCustomReportsGrid';
import { useGetStoredQuery } from 'customHooks/useGetStoredQuery';
import { useGetMainTotalQuery } from 'customHooks/useGetMainTotalQuery';
import PreviewContainer from 'components/newSetup/PreviewContainer';
import { PlaceholderType } from 'components/newSetup/Main';
import CustomReportResponsiveGridChart from './CustomReportResponsiveGridChart';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomReportKPIStyled } from './CustomReportKPI';
import DeleteConfirmationModal from 'components/DeleteConfirmationModall';
import { createPortal } from 'react-dom';

export type placeholderMetrics = {
  metric?: string;
  description?: string | null | undefined;
  view?: (string | null)[] | null | undefined;
  isSelected?: boolean;
  detailedMetric?: metric;
};

export type placeholderDimensions = {
  dimension?: string;
  description?: string | null | undefined;
  view?: (string | null)[] | null | undefined;
  isSelected?: boolean;
  detailedDimension?: detailedDimensionsDetail;
};

export type placeholderFullSources = {
  isSelected?: boolean;
  source_name?: string;
  metrics?: placeholderMetrics[];
  dimensions?: placeholderDimensions[];
  name: string;
  icon?: JSX.Element;
  metaName: string;
  calcMetrics: Array<calculatedMetricsDetail & { isSelected?: boolean }>;
  customConversions?: Array<CCData>;
};

type Props = {
  isGraph: boolean;
  sectionID: string | null | undefined;
  deleteItem?: (sectionID: string, sectionItemID: string) => void;
  handleModalType: (item: GridLayout | null, type: string | null) => void;
  layoutItem: GridLayout;
  initialQuery: queryValues | null;
  fetchQuery: (sectionItemID: string) => Promise<queryValues | null>;
  modal?: boolean;
  onQuerySaved?: (sectionItemID: string, query: queryValues) => void;
  modalType: string;
};

const CustomReportQuery = (props: Props) => {
  const {
    isGraph,
    sectionID,
    handleModalType,
    deleteItem,
    layoutItem,
    modal,
    onQuerySaved,
    initialQuery,
    fetchQuery,
    modalType,
  } = props;
  const theme = useTheme();
  const {
    activeCustomReport,
    dataSources,
    transformedACDetailsReport,
    sectionLayoutState,
    safeSectionLayoutUpdate,
    cacheTableData,
    getCachedTableData,
    clearTableDataCache,
    customReportCampaignList,
    setPublishPlaceholderObject,
    registerCleanupFunction,
    unregisterCleanupFunction,
  } = useCustomReportContext();
  const userIsAuthorized = activeCustomReport?.authorized;
  // ID za cache
  const cacheID = layoutItem.SectionItemID || '';

  // Provjeri ima li podataka u cache-u
  const cachedData = cacheID ? getCachedTableData(cacheID) : null;

  // Stanje za praćenje toka (NEW za početni izbor, OLD za prikaz rezultata, BACK za povratak, APPLY za primjenu promjena)
  const [newQuery, setNewQuery] = useState<'OLD' | 'NEW' | 'BACK' | 'APPLY'>(cachedData ? 'OLD' : 'NEW');

  const [test, setTest] = useState<any>();
  // if using cache, we skip queries - ali NE kad korisnik klikne APPLY
  const skipQueries =
    (Boolean(cachedData) && newQuery !== 'APPLY') || (!activeCustomReport?.authorized && test?.outputlocation);

  const { customReportsList } = useCampaignContext();
  const [saveNewSectionItem] = useSaveNewSectionItem();
  const activeCampaign = transformedACDetailsReport;
  const [selectedDataSource, setSelectedDataSource] = useState<placeholderFullSources>();
  const [chartMetrics, setChartMetrics] = useState<Array<MetricsResponse>>(
    initialQuery?.metrics?.filter((metric): metric is MetricsResponse => metric !== null) || [],
  );
  const { user } = useContext(AuthContext);

  const [userMainQuery, setUserMainQuery] = useState<queryInput>();
  const [userMainQueryTotal, setUserMainQueryTotal] = useState<queryInput>();
  const [userCCQuery, setUserCCQuery] = useState<queryInput>();
  const [userCCTotalQuery, setUserCCTotalQuery] = useState<queryInput>();
  const [userBenchmarkQuery, setUserBenchmarkQuery] = useState<queryInput>();
  const [userBenchmarkTotalQuery, setUserBenchmarkTotalQuery] = useState<queryInput>();

  const [title, setTitle] = useState<string>(initialQuery?.title || '');
  const [page, setPage] = useState<number>(modal ? 6 : 7);

  // Benchmark Index state
  const [benchmarkIndex, setBenchmarkIndex] = useState<BenchmarkingIndexValues | null>(test?.benchmarkingIndex || null);

  // Hide total state - initialized with backend value
  const [hideTotal, setHideTotal] = useState<boolean>(initialQuery?.hideTotal ?? false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedKPIRow, setSelectedKPIRow] = useState<Array<{ column: string; value: string | number }> | null>(null);
  // Sentinel value meaning "sum all rows" — no dimension filter applied
  const KPI_SUM_SENTINEL: Array<{ column: string; value: string | number }> = [{ column: '__SUM__', value: '__SUM__' }];
  const isKPISumSelected = selectedKPIRow?.length === 1 && selectedKPIRow[0].column === '__SUM__';
  // Draft metric name for KPI — updated freely without touching userMainQuery
  const [kpiNameDraft, setKpiNameDraft] = useState<string>(initialQuery?.title || '');
  //TODO published data
  const [publishedData] = useGetCustomReportPublishedData();
  // const hasLoadedPublishedData = useRef(false);

  useEffect(() => {
    if (!activeCustomReport?.authorized && layoutItem.SectionItemID) {
      // Dodaj provjeru da se ne pokreće nepotrebno
      if (test?.outputlocation) {
        // console.log(`Skipping publishedData for ${layoutItem.SectionItemID} - already have data`);
        return; // Već imamo podatke
      }

      // console.log(`Fetching published data for ${layoutItem.SectionItemID}`);
      publishedData(layoutItem.SectionItemID).then((data) => {
        if (data) {
          setTest(data);
          // console.log(`Published data loaded for ${layoutItem.SectionItemID}:`, data);
          // if (data?.metrics) {
          //   setChartMetrics(data.metrics.filter((metric): metric is MetricsResponse => metric !== null));
          // }
        } else {
          safeSectionLayoutUpdate((prev) => {
            return prev.map((section) => {
              if (section.SectionID === sectionID) {
                return {
                  ...section,
                  SectionElements: section.SectionElements?.filter(
                    (item) => item?.SectionItemID !== layoutItem.SectionItemID,
                  ),
                };
              }
              return section;
            });
          });
        }
      });
    }
  }, [
    activeCustomReport,
    layoutItem.SectionItemID,
    publishedData,
    sectionID,
    safeSectionLayoutUpdate,
    test?.outputlocation,
  ]);

  // Koristimo kondicionalne upite da izbjegnemo nepotrebno dohvaćanje podataka
  const [mainData, mainQueryStatus, , mainQueryID, mainOL] = useDataQuery({
    publishedOutputLocation: test?.outputlocation,
    publishedQueriesDetails: test,
    query: skipQueries ? undefined : userMainQuery,
    querySource: 'QUERY',
  });

  const [mainTotalData, mainTotalQueryStatus, , , mainOLTotal] = useDataQuery({
    publishedOutputLocation: test?.outputlocationTotal,
    publishedQueriesDetails: test,
    query: skipQueries ? undefined : userMainQueryTotal,
    querySource: 'QUERY TOTAL',
  });

  const [ccData, ccQueryStatus, , ccQueryID, ccOL] = useDataQuery({
    query: skipQueries ? undefined : userCCQuery,
    publishedOutputLocation: test?.outputlocation_cc,
    publishedQueriesDetails: test,
    querySource: 'QUERY',
  });

  const [ccTotalData, ccTotalQueryStatus, , , ccOLTotal] = useDataQuery({
    query: skipQueries ? undefined : userCCTotalQuery,
    publishedQueriesDetails: test,
    publishedOutputLocation: test?.outputlocationTotal_cc,
    querySource: 'QUERY TOTAL',
  });
  const [benchmarkData, benchmarkQueryStatus, , benchmarkQueryID, benchmarkOL] = useDataQuery({
    query: skipQueries ? undefined : userBenchmarkQuery,
    publishedOutputLocation: test?.outputlocation_benchmark,
    publishedQueriesDetails: test,
    querySource: 'QUERY',
  });
  const [benchmarkTotalData, benchmarkTotalQueryStatus] = useDataQuery({
    query: skipQueries ? undefined : userBenchmarkTotalQuery,
    publishedOutputLocation: test?.outputlocationTotal_benchmark,
    publishedQueriesDetails: test,
    querySource: 'QUERY TOTAL',
  });

  // Function to create benchmark query from main query
  /* const createBenchmarkQuery = useCallback(
    (benchmarkStartDate: Date, benchmarkEndDate: Date, targetMetricColumn: string) => {
      if (!userMainQuery) return null;

      const benchmarkQueryCopy = JSON.parse(JSON.stringify(userMainQuery));

      // Keep only the metric with column equal to targetMetricColumn (e.g., "cost" for CPM)
      const filteredMetrics = benchmarkQueryCopy.query.metrics.filter(
        (metric: any) => metric.column === targetMetricColumn,
      );
      benchmarkQueryCopy.query.metrics = filteredMetrics;

      // Filter detailedDimensions based on benchmarkable flag from selectedDataSource
      if (selectedDataSource?.dimensions) {
        benchmarkQueryCopy.query.detailedDimensions = benchmarkQueryCopy.query.detailedDimensions.filter(
          (queryDimension: any) => {
            // Find matching dimension in selectedDataSource
            const matchingSourceDimension = selectedDataSource?.dimensions?.find(
              (sourceDimension) => sourceDimension.dimension === queryDimension.column,
            );

            // If we found a matching dimension, check if it's benchmarkable
            if (matchingSourceDimension) {
              // Check if benchmarkable exists directly on the dimension
              if (matchingSourceDimension.hasOwnProperty('benchmarkable')) {
                return (matchingSourceDimension as any).benchmarkable !== false;
              }
              // Check if benchmarkable exists in detailedDimension
              if (
                matchingSourceDimension.detailedDimension &&
                (matchingSourceDimension.detailedDimension as any).hasOwnProperty('benchmarkable')
              ) {
                return (matchingSourceDimension.detailedDimension as any).benchmarkable !== false;
              }
            }

            // If no matching dimension found or no benchmarkable property, keep it (default behavior)
            return true;
          },
        );
      }

      // Update orderBy to contain only IDs from filtered metrics and remaining dimensions
      const metricIds = filteredMetrics.map((metric: any) => metric.id);
      const dimensionIds = benchmarkQueryCopy.query.detailedDimensions.map((dim: any) => dim.id);
      benchmarkQueryCopy.query.orderBy = benchmarkQueryCopy.query.orderBy.filter(
        (id: string) =>
          metricIds.includes(id) ||
          dimensionIds.includes(id) ||
          benchmarkQueryCopy.query.calculatedMetrics?.some((calc: any) => calc.id === id),
      );

      // Remove campaign_id filter if it exists
      if (benchmarkQueryCopy.query.filters) {
        benchmarkQueryCopy.query.filters = benchmarkQueryCopy.query.filters.filter(
          (filter: any) => filter.column !== 'campaign_id',
        );
      }

      // Add client_code filter
      const clientCodeFilter = {
        column: 'client_code',
        filter: {
          filtertype: 'EQUAL' as any,
          datatype: 'STRING' as any,
          value: benchmarkQueryCopy.clients[0].client,
        },
      };

      // Add date filter
      const dateFilter = {
        column: 'date',
        filter: {
          datatype: 'DATE' as any,
          filtertype: 'BETWEEN' as any,
          from: benchmarkStartDate.toISOString().split('T')[0],
          to: benchmarkEndDate.toISOString().split('T')[0],
        },
      };

      // Add filters to query
      if (!benchmarkQueryCopy.query.filters) {
        benchmarkQueryCopy.query.filters = [];
      }
      benchmarkQueryCopy.query.filters.push(clientCodeFilter, dateFilter);

      return benchmarkQueryCopy;
    },
    [userMainQuery, selectedDataSource],
  ); */

  // Handle benchmark query updates from PreviewContainer
  const handleBenchmarkQueryUpdate = useCallback((newBenchmarkQuery: any) => {
    if (newBenchmarkQuery) {
      setUserBenchmarkQuery(newBenchmarkQuery);
    } else {
      setUserBenchmarkQuery(undefined);
    }
  }, []);

  const prevUserMainQueryRef = useRef<queryInput>();

  // Sync benchmark query dimension names with main query
  useEffect(() => {
    // Check if main query actually changed (not just reference)
    const mainQueryChanged = prevUserMainQueryRef.current
      ? JSON.stringify(prevUserMainQueryRef.current) !== JSON.stringify(userMainQuery)
      : false;

    if (userMainQuery && userBenchmarkQuery && mainQueryChanged) {
      const updatedBenchmarkQuery = { ...userBenchmarkQuery };

      let hasChanges = false;
      updatedBenchmarkQuery.query.detailedDimensions = updatedBenchmarkQuery.query.detailedDimensions?.map(
        (benchmarkDim: any) => {
          // Find dimension with same column in the updated main query
          const updatedDimension = userMainQuery.query.detailedDimensions?.find(
            (mainDim: any) => mainDim?.column === benchmarkDim?.column,
          );

          if (updatedDimension && updatedDimension.name !== benchmarkDim.name) {
            // If name has changed, update the benchmark dimension
            hasChanges = true;
            return { ...benchmarkDim, name: updatedDimension.name };
          }

          return benchmarkDim;
        },
      );

      if (hasChanges) {
        // setUserBenchmarkQuery(updatedBenchmarkQuery);
        // setUserBenchmarkTotalQuery(updatedBenchmarkQuery);
      }
    }

    // Update the ref for next comparison
    if (userMainQuery) {
      prevUserMainQueryRef.current = userMainQuery;
    }
  }, [userMainQuery, userBenchmarkQuery]);

  // Dohvati inicijalni upit ako postoji
  useEffect(() => {
    if (
      !initialQuery &&
      activeCustomReport?.authorized &&
      layoutItem.SectionItemID &&
      fetchQuery &&
      newQuery !== 'APPLY'
    ) {
      fetchQuery(layoutItem.SectionItemID).then((query) => {
        if (query) {
          setNewQuery('OLD');
          setChartMetrics((query.metrics || []).filter((metric): metric is MetricsResponse => metric !== null));
          if (query.title) {
            setTitle(query.title);
            setKpiNameDraft(query.title);
          }
        }
      });
    }
  }, [initialQuery, layoutItem.SectionItemID, fetchQuery, activeCustomReport?.authorized]);

  // Spojeni podaci su ili iz cache-a ili iz hook-a
  const [joinedDataFromHook] = useJoinData(
    skipQueries && !test ? undefined : (mainData as any[] | undefined),
    skipQueries && !test ? undefined : (mainTotalData as any[] | undefined),
    skipQueries && !test ? undefined : (ccData as any[] | undefined),
    skipQueries && !test ? undefined : (ccTotalData as any[] | undefined),
    skipQueries && !test ? undefined : (benchmarkData as any[] | undefined),
    skipQueries && !test ? undefined : (benchmarkTotalData as any[] | undefined),
    isGraph,
    skipQueries && !test ? 'DONE' : mainQueryStatus,
    skipQueries && !test ? 'DONE' : mainTotalQueryStatus,
    skipQueries && !test ? 'DONE' : ccQueryStatus,
    skipQueries && !test ? 'DONE' : ccTotalQueryStatus,
    /* 
    skipQueries && !test ? 'DONE' : */ benchmarkQueryStatus,
    /*  skipQueries && !test ? 'DONE' : */ benchmarkTotalQueryStatus,
    chartMetrics,
    setChartMetrics,
    sectionID || '',
    newQuery,
    false,
    benchmarkIndex, // Add benchmark index for proper calculation
  );

  // Spojeni podaci su ili iz cache-a ili iz hook-a
  const joinedData = skipQueries ? cachedData : joinedDataFromHook;

  // Definiramo isLoading na osnovu cache-a ili trenutnog statusa upita
  const isLoading = skipQueries
    ? false
    : [
        mainQueryStatus,
        mainTotalQueryStatus,
        ccQueryStatus,
        ccTotalQueryStatus,
        benchmarkQueryStatus,
        benchmarkTotalQueryStatus,
      ].some((el) => el === 'RUNNING');

  // After loading is complete and we have new data, cache it and reset query state
  const joinedDataLength = joinedDataFromHook?.length || 0;
  const prevJoinedDataLengthRef = useRef(joinedDataLength);

  useEffect(() => {
    if (newQuery === 'APPLY' && !isLoading && joinedDataFromHook && joinedDataLength > 0) {
      // Cache the new data
      if (cacheID) {
        cacheTableData(cacheID, [...joinedDataFromHook]);
      }

      // Set query state to OLD — makes skipQueries=true so subsequent userMainQuery
      // updates (e.g. renaming metrics[0].name) don't fire new queries
      setNewQuery('OLD');
    }
  }, [newQuery, isLoading, joinedDataLength, cacheID, cacheTableData, joinedDataFromHook]);

  // Cache data when it's available (but not during modal mode)
  useEffect(() => {
    if (!modal && joinedDataFromHook && joinedDataLength > 0 && cacheID && !isLoading && newQuery !== 'APPLY') {
      // Only cache if data actually changed
      if (prevJoinedDataLengthRef.current !== joinedDataLength) {
        cacheTableData(cacheID, joinedDataFromHook);
        prevJoinedDataLengthRef.current = joinedDataLength;
      }
    }
  }, [cacheID, joinedDataLength, isLoading, modal, cacheTableData, newQuery, joinedDataFromHook]);

  // Dohvati spremljene upite
  const [mainQuery] = useGetStoredQuery(initialQuery?.query_id as string);
  const [mainQueryTotal] = useGetMainTotalQuery(mainQuery as queryInput, 'MAIN', initialQuery?.hideTotal || false);
  const [ccQuery] = useGetStoredQuery(initialQuery?.query_id_cc as string);
  const [ccQueryTotal] = useGetMainTotalQuery(ccQuery as queryInput, 'CC');
  const [benchmarkQuery] = useGetStoredQuery(initialQuery?.query_id_benchmark as string);
  const [benchmarkTotalQuery] = useGetMainTotalQuery(benchmarkQuery as queryInput, 'BENCHMARK');
  // const benchmarkQuery = undefined;
  const updatePublishPlaceholderObject = useCallback(
    (placeholdersObject: any, id?: string) => {
      const IdtoUse = id || layoutItem.SectionItemID || '';
      setPublishPlaceholderObject((prev) => {
        if (prev[sectionID || '']) {
          // Update existing object
          return {
            ...prev,
            [IdtoUse || '']: {
              ...prev[IdtoUse || ''],
              ...placeholdersObject,
            },
          };
        } else {
          // Add new object
          return {
            ...prev,
            [IdtoUse || '']: placeholdersObject,
          };
        }
      });
    },
    [layoutItem.SectionItemID, sectionID, setPublishPlaceholderObject],
  );

  useEffect(() => {
    // updata olarray was for some reason inside if statement. I didnt delete it because I am not sure if it is needed
    // update olarray used to be inside of the if statemetn, i kept the code because i am not sure if it will work
    if (
      ![mainQueryStatus, mainTotalQueryStatus, ccQueryStatus, ccTotalQueryStatus, benchmarkQueryStatus].includes(
        'RUNNING',
      ) &&
      ![mainQueryStatus, mainTotalQueryStatus, ccQueryStatus, ccTotalQueryStatus, benchmarkQueryStatus].includes(
        'FAILED',
      ) &&
      layoutItem.SectionItemID
    ) {
      if (!isGraph) {
        if (ccQueryStatus === 'NOT STARTED') {
          if (mainOL && mainOLTotal) {
            const placeholdersObject = {
              position: layoutItem.SectionItemID,
              outputlocation: mainOL,
              outputlocationTotal: mainOLTotal,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: initialQuery?.title ?? title,
            };
            updatePublishPlaceholderObject(placeholdersObject);
          }
        } else if (ccQueryStatus === 'DONE' && ccTotalQueryStatus === 'DONE') {
          if (mainOL && mainOLTotal && ccOL && ccOLTotal) {
            const placeholdersObject = {
              position: layoutItem.SectionItemID,
              outputlocation: mainOL,
              outputlocationTotal: mainOLTotal,
              outputlocation_cc: ccOL,
              outputlocationTotal_cc: ccOLTotal,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: initialQuery?.title ?? title,
            };
            updatePublishPlaceholderObject(placeholdersObject);
          }
        }
      } else if (isGraph /* && chartMetrics.length > 0 */) {
        if (ccQueryStatus === 'NOT STARTED') {
          if (mainOL) {
            const placeholdersObject = {
              position: layoutItem.SectionItemID,
              outputlocation: mainOL,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: initialQuery?.title ?? title,
              metrics: chartMetrics.forEach((metric) => {
                const { __typename, ...rest } = metric;
                return rest;
              }),
            };
            updatePublishPlaceholderObject(placeholdersObject);
          }
        } else if (ccQueryStatus === 'DONE') {
          if (mainOL && ccOL) {
            const placeholdersObject = {
              position: layoutItem.SectionItemID,
              outputlocation: mainOL,
              outputlocation_cc: ccOL,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: initialQuery?.title ?? title,
              metrics: chartMetrics.forEach((metric) => {
                const { __typename, ...rest } = metric;
                return rest;
              }),
            };
            updatePublishPlaceholderObject(placeholdersObject);
          }
        }
      }
    }
  }, [
    ccOL,
    ccOLTotal,
    ccQueryStatus,
    ccTotalQueryStatus,
    benchmarkOL,
    benchmarkQueryStatus,
    chartMetrics,
    isGraph,
    mainOL,
    mainOLTotal,
    mainQueryStatus,
    mainTotalQueryStatus,
    newQuery,
    sectionID,
    title,
    page,
    initialQuery?.title,
    layoutItem.SectionItemID,
    updatePublishPlaceholderObject,
  ]);

  // Postavi dohvaćene upite ako postoje
  useEffect(() => {
    if (mainQuery) {
      setUserMainQuery(mainQuery);
    }
  }, [mainQuery]);

  useEffect(() => {
    if (mainQueryTotal) {
      setUserMainQueryTotal(mainQueryTotal);
    }
  }, [mainQueryTotal]);

  useEffect(() => {
    if (ccQuery) {
      setUserCCQuery(ccQuery);
    }
  }, [ccQuery]);

  useEffect(() => {
    if (ccQueryTotal) {
      setUserCCTotalQuery(ccQueryTotal);
    }
  }, [ccQueryTotal]);

  useEffect(() => {
    if (benchmarkQuery) {
      setUserBenchmarkQuery(benchmarkQuery);
    }
  }, [benchmarkQuery]);
  useEffect(() => {
    if (benchmarkTotalQuery) {
      setUserBenchmarkTotalQuery(benchmarkTotalQuery);
    }
  }, [benchmarkTotalQuery]);

  // Resetiraj upite kada se promijeni aktivni izvještaj
  useEffect(() => {
    setUserMainQuery(undefined);
    setUserMainQueryTotal(undefined);
    setUserCCQuery(undefined);
    setUserCCTotalQuery(undefined);
    setUserBenchmarkQuery(undefined);
    setUserBenchmarkTotalQuery(undefined);
  }, [activeCustomReport]);

  // Resetiraj odabrani izvor podataka kada se promijene dostupni izvori
  useEffect(() => {
    setSelectedDataSource(undefined);
  }, [dataSources]);

  // Postavimo selectedDataSource iz userMainQuery ako imamo podatke i kliknemo Back
  useEffect(() => {
    if (newQuery === 'BACK' && userMainQuery && dataSources) {
      // Stvori selectedDataSource iz userMainQuery da sačuvamo odabire
      const lookupPlaceHolderName = (viewName: string) => {
        if (viewName.includes('adform')) {
          return 'Adform';
        } else if (viewName.includes('facebook')) {
          return 'Facebook';
        } else if (viewName.includes('google_ads_video')) {
          return 'Youtube';
        } else if (viewName.includes('google_ads_display')) {
          return 'Google Ads';
        } else if (viewName.includes('campaign_manager')) {
          return 'CM';
        } else if (viewName.includes('linkedin')) {
          return 'LinkedIn';
        } else if (viewName.includes('_tv_')) {
          return 'TV';
        } else if (viewName.includes('_radio_')) {
          return 'RADIO';
        } else if (viewName.includes('print')) {
          return 'PRINT';
        } else if (viewName.includes('outdoor')) {
          return 'OOH';
        } else if (viewName.includes('cinema')) {
          return 'CINEMA';
        } else if (viewName.includes('snapchat')) {
          return 'Snapchat';
        } else if (viewName.includes('tiktok')) {
          return 'TikTok';
        } else if (viewName.includes('readpeak')) {
          return 'readpeak';
        } else if (viewName.includes('taboola')) {
          return 'Taboola';
        } else if (viewName.includes('pinterest')) {
          return 'Pinterest';
        } else if (viewName.includes('marathon')) {
          return 'Marathon';
        } else if (viewName.includes('campaign_manager_olv')) {
          return 'Online Video';
        } else if (viewName.includes('adform_olv')) {
          return 'Online Video';
        } else if (viewName.endsWith('google_dv360_youtube_')) {
          return 'DV360(YouTube)';
        } else if (
          viewName.includes('google_dv360_youtube_excluded_') ||
          viewName.includes('google_dv360_conversions')
        ) {
          return 'DV360';
        } else if (viewName.includes('Custom')) {
          return 'Custom';
        } else {
          return 'Unknown';
        }
      };
      const slicedViewNamesArray = userMainQuery?.views.map((el) => {
        const parts = el?.viewName.split('_') as string[];
        return parts.length > 2 ? parts.slice(1, 2).join('_') : '';
      });
      const allItemsAreSame = slicedViewNamesArray?.every((item) => item === slicedViewNamesArray[0]);

      const activeSourceInfo = !allItemsAreSame ? 'Custom' : (userMainQuery?.views[0]?.viewName ?? '');
      const lookupName = lookupPlaceHolderName(activeSourceInfo);
      const currentSource =
        lookupName === 'Unknown'
          ? dataSources.filter(
              (source) => source.name === lookupPlaceHolderName(userMainQuery?.views[0]?.viewName ?? ''),
            )[0]
          : (dataSources.filter((source) => source.name === lookupName)[0] as placeholderFullSources);

      if (currentSource) {
        const clonedSource = structuredClone(currentSource) as placeholderFullSources;

        // Označi odabrane dimenzije
        if (clonedSource.dimensions) {
          clonedSource.dimensions = clonedSource.dimensions.map((dim) => {
            const isSelected = userMainQuery.query.detailedDimensions?.some((d) => d?.column === dim.dimension);

            if (isSelected) {
              const detailedDim = userMainQuery.query.detailedDimensions?.find((d) => d?.column === dim.dimension);

              return {
                ...dim,
                isSelected: true,
                detailedDimension: detailedDim || {
                  column: dim.dimension || '',
                  hidden: false,
                  id: Math.floor(Math.random() * Date.now()).toString(),
                  name: dim.dimension || '',
                },
              };
            }

            return dim;
          });
        }

        // Označi odabrane metrike
        if (clonedSource.metrics) {
          clonedSource.metrics = clonedSource.metrics.map((metric) => {
            const isSelected = userMainQuery.query.metrics?.some((m) => m?.column === metric.metric);

            if (isSelected) {
              const detailedMetric = userMainQuery.query.metrics?.find((m) => m?.column === metric.metric);

              return {
                ...metric,
                isSelected: true,
                detailedMetric: detailedMetric || {
                  column: metric.metric || '',
                  name: metric.metric || '',
                  aggregation: metricFunction.SUM,
                  id: Math.floor(Math.random() * Date.now()).toString(),
                  decimals: 2,
                },
              };
            }

            return metric;
          });
        }

        // Označi odabrane izračunate metrike
        if (clonedSource.calcMetrics) {
          clonedSource.calcMetrics = clonedSource.calcMetrics.map((calcMetric) => {
            const isSelected = userMainQuery.query.calculatedMetrics?.some((cm) => cm?.query === calcMetric.query);

            return {
              ...calcMetric,
              isSelected: !!isSelected,
              id: Math.floor(Math.random() * Date.now()).toString(),
            };
          });
        }

        // Označi odabrane prilagođene konverzije
        if (clonedSource.customConversions && userCCQuery) {
          clonedSource.customConversions = clonedSource.customConversions.map((cc) => {
            return {
              ...cc,
              isSelected: !!userCCQuery,
            };
          });
        }

        clonedSource.isSelected = true;
        setSelectedDataSource(clonedSource);

        // Postavi odabrane platforme za Back scenarij
        if (activeSourceInfo === 'Custom') {
          // Za Custom izvor, identificiraj sve platforme iz views
          const selectedPlatformNames = Array.from(
            new Set(userMainQuery.views.map((view) => lookupPlaceHolderName(view?.viewName ?? ''))),
          );
          setSelectedPlatforms(selectedPlatformNames);
        } else {
          // Za pojedinačnu platformu
          setSelectedPlatforms([currentSource.name]);
        }

        // Hydrate selectedKPIRow from existing EQUAL filters on non-campaign_id dimensions
        if (modalType === 'kpiQuery') {
          // If no dimensions in the query → was saved as SUM
          if ((userMainQuery.query.detailedDimensions?.length ?? 0) === 0) {
            setSelectedKPIRow([{ column: '__SUM__', value: '__SUM__' }]);
          } else if (userMainQuery.query.filters) {
            const kpiFilters = userMainQuery.query.filters.filter(
              (f) =>
                f?.column &&
                f.column !== 'campaign_id' &&
                f.column !== 'date' &&
                (f.filter as any)?.filtertype === 'EQUAL' &&
                (f.filter as any)?.value !== undefined,
            );
            if (kpiFilters.length > 0) {
              setSelectedKPIRow(kpiFilters.map((f) => ({ column: f!.column, value: (f!.filter as any).value })));
            }
          }
        }
      }
    }
  }, [newQuery, userMainQuery, userCCQuery, dataSources, modalType]);

  // Dodajemo novo stanje za praćenje odabranih platformi
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  // Resetiraj selectedPlatforms i selectedKPIRow kada je novi query
  useEffect(() => {
    if (newQuery === 'NEW') {
      setSelectedPlatforms([]);
      setSelectedKPIRow(null);
      setKpiNameDraft('');
    }
  }, [newQuery]);

  // Register cleanup function to clear query state when custom report is closed
  useEffect(() => {
    const queryId = `query-${layoutItem.SectionItemID}`;

    const cleanupQueryState = () => {
      setTest(undefined);
      setNewQuery('NEW');
      setChartMetrics([]);
      setUserMainQuery(undefined);
      setUserMainQueryTotal(undefined);
      setUserCCQuery(undefined);
      setUserCCTotalQuery(undefined);
      setUserBenchmarkQuery(undefined);
      setUserBenchmarkTotalQuery(undefined);
      setTitle('');
      setSelectedDataSource(undefined);
      setSelectedPlatforms([]);
      setSelectedKPIRow(null);
      setKpiNameDraft('');
    };

    registerCleanupFunction(queryId, cleanupQueryState);

    // Cleanup when component unmounts
    return () => {
      unregisterCleanupFunction(queryId);
    };
  }, [layoutItem.SectionItemID, registerCleanupFunction, unregisterCleanupFunction]);

  // SELECT SOURCE
  const selectSource = (event: ChangeEvent<HTMLInputElement>) => {
    const platformName = event.target.value;

    if (event.target.checked) {
      // Dodaj platformu
      const newSelectedPlatforms = [...selectedPlatforms, platformName];
      setSelectedPlatforms(newSelectedPlatforms);

      if (newSelectedPlatforms.length === 1) {
        // Jedna platforma odabrana
        const selectedSource = dataSources.find((source) => source.name === platformName);
        if (selectedSource) {
          setSelectedDataSource(structuredClone(selectedSource) as placeholderFullSources);
        }
      } else {
        // Više platformi odabrano - stvori Custom izvor
        const customSource = updateCustomSource(
          dataSources.map((source) => ({
            ...structuredClone(source),
            isSelected: newSelectedPlatforms.includes(source.name),
          })) as placeholderFullSources[],
        );
        const customSourceObject = customSource.find((el) => el.source_name === 'DATASOURCE#CUSTOMSOURCE');
        setSelectedDataSource(customSourceObject as placeholderFullSources);
      }
    } else {
      // Ukloni platformu
      const newSelectedPlatforms = selectedPlatforms.filter((name) => name !== platformName);
      setSelectedPlatforms(newSelectedPlatforms);

      if (newSelectedPlatforms.length === 0) {
        // Nema odabranih platformi
        setSelectedDataSource(undefined);
      } else if (newSelectedPlatforms.length === 1) {
        // Jedna platforma ostala
        const selectedSource = dataSources.find((source) => source.name === newSelectedPlatforms[0]);
        if (selectedSource) {
          setSelectedDataSource(structuredClone(selectedSource) as placeholderFullSources);
        }
      } else {
        // Više platformi još uvijek odabrano
        const customSource = updateCustomSource(
          dataSources.map((source) => ({
            ...structuredClone(source),
            isSelected: newSelectedPlatforms.includes(source.name),
          })) as placeholderFullSources[],
        );
        const customSourceObject = customSource.find((el) => el.source_name === 'DATASOURCE#CUSTOMSOURCE');
        setSelectedDataSource(customSourceObject as placeholderFullSources);
      }
    }
  };

  // SELECT DIMENSION
  const selectDimension = (event: ChangeEvent<HTMLInputElement>) => {
    const addDimension = () => {
      const detailedDimension = {
        column: event.target.name,
        hidden: false,
        id: Math.floor(Math.random() * Date.now()).toString(),
        name: event.target.name,
      };

      const newDimensions = selectedDataSource?.dimensions?.map((el) => {
        if (el.dimension === event.target.value) {
          return {
            ...el,
            isSelected: true,
            detailedDimension: detailedDimension,
          };
        } else {
          return el;
        }
      });
      selectedDataSource &&
        setSelectedDataSource({
          ...selectedDataSource,
          dimensions: newDimensions,
        });
    };

    const removeDimension = () => {
      const newDimensions = selectedDataSource?.dimensions?.map((el) => {
        if (el.dimension === event.target.value) {
          delete el.detailedDimension;
          return {
            ...el,
            isSelected: false,
          };
        } else {
          return el;
        }
      });
      selectedDataSource &&
        setSelectedDataSource({
          ...selectedDataSource,
          dimensions: newDimensions,
        });
    };

    event.target.checked ? addDimension() : removeDimension();
  };

  // SELECT METRIC
  const selectMetric = (event: ChangeEvent<HTMLInputElement>) => {
    const addMetric = () => {
      const detailedMetric: metric = {
        column: event.target.name === 'End date' || event.target.name === 'Start date' ? 'date' : event.target.name,
        name: event.target.name,
        aggregation:
          event.target.name.endsWith('_reach') ||
          event.target.name.startsWith('sub_rf_') ||
          event.target.name === 'effective_frequency' ||
          event.target.name === 'reach' ||
          event.target.name === 'frequency' ||
          event.target.name === 'date' ||
          event.target.name === 'End date'
            ? metricFunction.MAX
            : event.target.name === 'Start date'
              ? metricFunction.MIN
              : metricFunction.SUM,
        id: Math.floor(Math.random() * Date.now()).toString(),
        decimals: event.target.name === 'Start date' || event.target.name === 'End date' ? -1 : 2,
      };

      const newMetrics = selectedDataSource?.metrics?.map((el) => {
        if (el.metric === event.target.value) {
          return {
            ...el,
            isSelected: true,
            detailedMetric: detailedMetric,
          };
        } else {
          // For kpiQuery, deselect all other metrics when one is picked
          if (modalType === 'kpiQuery') {
            const cloned = { ...el };
            delete cloned.detailedMetric;
            return { ...cloned, isSelected: false };
          }
          return el;
        }
      });
      selectedDataSource &&
        setSelectedDataSource({
          ...selectedDataSource,
          metrics: newMetrics,
        });
    };

    const removeMetric = () => {
      const newMetrics = selectedDataSource?.metrics?.map((el) => {
        if (el.metric === event.target.value) {
          delete el.detailedMetric;
          return {
            ...el,
            isSelected: false,
          };
        } else {
          return el;
        }
      });
      selectedDataSource &&
        setSelectedDataSource({
          ...selectedDataSource,
          metrics: newMetrics,
        });
    };

    event.target.checked ? addMetric() : removeMetric();
  };

  // SELECT CALC METRIC
  const selectCalcMetric = (metric: ChangeEvent<HTMLInputElement>) => {
    const addCalcMetric = () => {
      const newCalcMetrics =
        selectedDataSource?.calcMetrics?.map((el) => {
          if (el.query === metric.target.value) {
            return {
              ...el,
              isSelected: true,
              decimals: 2,
              id: Math.floor(Math.random() * Date.now()).toString(),
            };
          } else {
            return el;
          }
        }) || [];

      if (selectedDataSource) {
        setSelectedDataSource({
          ...selectedDataSource,
          calcMetrics: newCalcMetrics,
        });
      }
    };

    const removeCalcMetric = () => {
      const newCalcMetrics =
        selectedDataSource?.calcMetrics?.map((el) => {
          if (el.query === metric.target.value) {
            delete el.decimals;
            return {
              ...el,
              isSelected: false,
            };
          } else {
            return el;
          }
        }) || [];

      if (selectedDataSource) {
        setSelectedDataSource({
          ...selectedDataSource,
          calcMetrics: newCalcMetrics,
        });
      }
    };

    metric.target.checked ? addCalcMetric() : removeCalcMetric();
  };

  // SELECT CC
  const selectCustomConversion = (event: ChangeEvent<HTMLInputElement>) => {
    const addCC = () => {
      const newCC = selectedDataSource?.customConversions?.map((el) => {
        if (el.CCname === event.target.name) {
          return {
            ...el,
            isSelected: true,
          };
        } else {
          return el;
        }
      });
      if (selectedDataSource) {
        setSelectedDataSource({
          ...selectedDataSource,
          customConversions: newCC,
        });
      }
    };

    const removeCC = () => {
      const newCC = selectedDataSource?.customConversions?.map((el) => {
        if (el.CCname === event.target.name) {
          return {
            ...el,
            isSelected: false,
          };
        } else {
          return el;
        }
      });
      if (selectedDataSource) {
        setSelectedDataSource({
          ...selectedDataSource,
          customConversions: newCC,
        });
      }
    };

    event.target.checked ? addCC() : removeCC();
  };

  const nextPage = () => setPage((prev) => prev + 1);
  const previousPage = () => setNewQuery('BACK');

  const element = {
    type: isGraph ? 'Chart' : 'Table',
    minor: Number(layoutItem.GridPosition?.i),
    id: layoutItem.SectionItemID || '',
  } as PlaceholderType;
  // const reportHasDateFilter =
  //   customReportsList
  //     ?.find((report) => report?.CustomReportID === activeCampaign?.campaignId)
  //     ?.Filters?.some((filter) => filter?.column === 'date') || false;
  const reportDateFilter = customReportsList
    ?.find((report) => report?.CustomReportID === activeCampaign?.campaignId)
    ?.Filters?.find((filter) => filter?.column === 'date');

  //PREP USER QUERY ON FETCH
  const prepUserQuery = () => {
    // const customReportCampaigns = customReportsList?.find(
    //   (report) => report?.CustomReportID === activeCampaign?.campaignId,
    // )?.Filters?.[0]?.filter?.list; //TODO Tobias

    // Filter out null values to ensure we pass string[] | undefined
    // const filteredCustomReportCampaigns = customReportCampaigns
    //   ? customReportCampaigns.filter((item): item is string => item !== null)
    //   : undefined;
    const selectedPlatform = selectedDataSource;
    delete selectedPlatform?.icon;
    const copyofSelectedPlatform = structuredClone(selectedPlatform) as placeholderFullSources;
    const q = structuredClone(
      prepQuery(
        copyofSelectedPlatform as placeholderFullSources,
        isGraph,
        activeCampaign,
        user,
        customReportCampaignList,
        reportDateFilter,
      ),
    );

    //SET MAIN QUERY
    if (userMainQuery) {
      q.query.detailedDimensions = q.query.detailedDimensions?.map((d) => {
        const findDimension = userMainQuery.query.detailedDimensions?.find((el) => el?.column === d?.column);
        if (findDimension) {
          if (findDimension.hidden !== d?.hidden) {
            return { ...findDimension, hidden: d?.hidden as boolean, id: d?.id };
          } else {
            return { ...findDimension, id: d?.id };
          }
        } else {
          return d;
        }
      });

      q.query.metrics = q.query.metrics?.map((m) => {
        const findMetric = userMainQuery.query.metrics?.find((el) => el?.column === m?.column);
        if (findMetric) {
          return { ...findMetric, id: m?.id };
        } else {
          return m;
        }
      });

      q.query.filters = userMainQuery.query.filters
        ?.map((f) =>
          f?.column === 'campaign_id' ||
          selectedPlatform?.dimensions?.filter((d) => d?.isSelected).some((d) => d?.dimension === f?.column)
            ? f
            : null,
        )
        .filter((f) => f !== null) as any;
    }

    // KEEP RENAMED CALCULATED METRIC WHEN CLICKING BACK
    q.query.calculatedMetrics = q.query.calculatedMetrics?.map((m) => {
      const findMetric = userMainQuery?.query.calculatedMetrics?.find((el) => el?.query === m?.query);
      if (findMetric) {
        return { ...findMetric, id: m?.id };
      } else {
        return m;
      }
    });

    setUserMainQuery(q);

    if (
      selectedPlatform?.customConversions?.length !== 0 &&
      selectedPlatform?.customConversions?.some((el) => el.isSelected)
    ) {
      const CCq = prepQueryCC(
        selectedPlatform as placeholderFullSources,
        isGraph,
        activeCampaign,
        user,
        customReportCampaignList,
        reportDateFilter,
      );

      // SET CC QUERY
      if (userCCQuery) {
        CCq.query.metrics = CCq.query.metrics?.map((m) => {
          const findMetric = userCCQuery.query.metrics?.find((el) => el?.column === m?.column);
          if (findMetric) {
            return { ...findMetric, id: m?.id };
          } else {
            return m;
          }
        });

        CCq.query.detailedDimensions = CCq.query.detailedDimensions?.map((d) => {
          const findDimension = userCCQuery.query.detailedDimensions?.find((el) => el?.column === d?.column);
          if (findDimension) {
            return { ...findDimension, id: d?.id };
          } else {
            return d;
          }
        });
      }

      // KEEP RENAMED CALCULATED METRIC WHEN CLICKING BACK
      CCq.query.calculatedMetrics = CCq.query.calculatedMetrics?.map((m) => {
        const findMetric = userCCQuery?.query.calculatedMetrics?.find((el) => el?.query === m?.query);
        if (findMetric) {
          return { ...findMetric, id: m?.id };
        } else {
          return m;
        }
      });

      setUserCCQuery(CCq);
    } else {
      setUserCCQuery(undefined);
    }

    if (!isGraph) {
      const sourceObjectTotal = structuredClone(selectedPlatform) as placeholderFullSources;
      sourceObjectTotal.dimensions = sourceObjectTotal.dimensions?.map((d) => {
        if (d.dimension === 'client_code') {
          return {
            ...d,
            isSelected: true,
            detailedDimension: {
              column: 'client_code',
              hidden: false,
              id: Math.floor(Math.random() * Date.now()).toString(),
              name: 'campaign_id',
            },
          };
        } else {
          return { ...d, isSelected: false };
        }
      });

      const qTotal = prepQuery(
        sourceObjectTotal as placeholderFullSources,
        isGraph,
        activeCampaign,
        user,
        customReportCampaignList,
        reportDateFilter,
      );

      if (userMainQueryTotal) {
        qTotal.query.metrics = qTotal.query.metrics?.map((m) => {
          const findMetric = userMainQueryTotal.query.metrics?.find((el) => el?.column === m?.column);
          if (findMetric) {
            return { ...findMetric, id: m?.id };
          } else {
            return m;
          }
        });
      }

      qTotal.query.calculatedMetrics = qTotal.query.calculatedMetrics?.map((m) => {
        const findMetric = userMainQueryTotal?.query.calculatedMetrics?.find((el) => el?.query === m?.query);
        if (findMetric) {
          return { ...findMetric, id: m?.id };
        } else {
          return m;
        }
      });

      qTotal.query.calculatedMetrics = qTotal.query.calculatedMetrics?.map((element: any) =>
        element.query.includes('/MAX')
          ? {
              ...element,
              query: element.query.slice(0, element.query.indexOf('/')).concat('/MAX(#campaign_mapping_reach)'),
            }
          : element,
      );

      if (
        qTotal.query.filters?.some(
          (el) => el?.column === 'campaign_id' && (el?.filter as any)?.list && (el?.filter as any)?.list?.length > 1,
        )
      ) {
        qTotal.query.nullColumns = ['campaign_mapping_reach'];
      }

      const isFacebook = qTotal.views[0]?.viewName.includes('facebook') ? true : false;
      const isSnapchat = qTotal.views[0]?.viewName.includes('snapchat') ? true : false;
            const isDV360 = qTotal.views[0]?.viewName.includes('dv360')  ? true : false;

      qTotal.query.metrics = qTotal.query.metrics?.map((element: any) =>
        element.column.endsWith('reach') && (isFacebook || isSnapchat || isDV360)
          ? { ...element, column: 'campaign_mapping_reach' }
          : element,
      );

      setUserMainQueryTotal(qTotal);

      if (
        selectedPlatform?.customConversions?.length !== 0 &&
        selectedPlatform?.customConversions?.some((el) => el.isSelected)
      ) {
        const CCqTotal = prepQueryCC(
          sourceObjectTotal as placeholderFullSources,
          isGraph,
          activeCampaign,
          user,
          customReportCampaignList,
          reportDateFilter,
        );

        if (userCCTotalQuery) {
          CCqTotal.query.metrics = CCqTotal.query.metrics?.map((m) => {
            const findMetric = userCCTotalQuery.query.metrics?.find((el) => el?.column === m?.column);
            if (findMetric) {
              return { ...findMetric, id: m?.id };
            } else {
              return m;
            }
          });
        }

        // KEEP RENAMED CALCULATED METRIC WHEN CLICKING BACK
        CCqTotal.query.calculatedMetrics = CCqTotal.query.calculatedMetrics?.map((m) => {
          const findMetric = userCCTotalQuery?.query.calculatedMetrics?.find((el) => el?.query === m?.query);
          if (findMetric) {
            return { ...findMetric, id: m?.id };
          } else {
            return m;
          }
        });

        setUserCCTotalQuery(CCqTotal);
      }
    }

    // Postavimo newQuery na OLD nakon dohvaćanja podataka
    setNewQuery('APPLY');
    console.log('custom report query - prepUserQuery executed', q);

    return q;
  };
  console.log('ALL QUERIES', userMainQueryTotal);
  // HANDLE SAVE
  const [saveCustomReportQuery] = useSaveCustomReportQuery();
  const handleSave = async () => {
    const updateAndClose = (id: string) => {
      const updatedSectionLayoutState = sectionLayoutState.map((section) => {
        return {
          ...section,
          SectionElements: section.SectionElements?.map((el) => {
            if (el?.SectionItemID === id || el?.GridPosition?.i === layoutItem.GridPosition?.i) {
              return {
                ...el,
                Position: layoutItem.Position,
                SectionItemID: id,
                Open: false,
                __typename: 'GridLayout',
              } as GridLayout;
            }
            return el;
          }),
        };
      });

      if (onQuerySaved) {
        onQuerySaved(id, {
          query_id: mainQueryID,
          query_id_cc: ccQueryID,
          query_id_benchmark: benchmarkQueryID,
          title: title,
          metrics: chartMetrics ?? [],
        } as queryValues);
      }

      if (!isGraph) {
        if (ccQueryStatus === 'NOT STARTED') {
          if (mainOL && mainOLTotal) {
            const placeholdersObject = {
              position: id,
              outputlocation: mainOL,
              outputlocationTotal: mainOLTotal,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: title,
            };
            updatePublishPlaceholderObject(placeholdersObject, id);
          }
        } else if (ccQueryStatus === 'DONE' && ccTotalQueryStatus === 'DONE') {
          if (mainOL && mainOLTotal && ccOL && ccOLTotal) {
            const placeholdersObject = {
              position: id,
              outputlocation: mainOL,
              outputlocationTotal: mainOLTotal,
              outputlocation_cc: ccOL,
              outputlocationTotal_cc: ccOLTotal,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: title,
            };
            updatePublishPlaceholderObject(placeholdersObject, id);
          }
        }
      } else if (isGraph /* && chartMetrics.length > 0 */) {
        if (ccQueryStatus === 'NOT STARTED') {
          if (mainOL) {
            const placeholdersObject = {
              position: id,
              outputlocation: mainOL,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: title,
              metrics: chartMetrics.forEach((metric) => {
                const { __typename, ...rest } = metric;
                return rest;
              }),
            };
            updatePublishPlaceholderObject(placeholdersObject, id);
          }
        } else if (ccQueryStatus === 'DONE') {
          if (mainOL && ccOL) {
            const placeholdersObject = {
              position: id,
              outputlocation: mainOL,
              outputlocation_cc: ccOL,
              ...(benchmarkOL && { outputlocation_benchmark: benchmarkOL }),
              ...(benchmarkIndex && { benchmarkingIndex: benchmarkIndex }),
              title: title,
              metrics: chartMetrics.forEach((metric) => {
                const { __typename, ...rest } = metric;
                return rest;
              }),
            };
            updatePublishPlaceholderObject(placeholdersObject, id);
          }
        }
      }

      // KLJUČNO: Spremi podatke u cache pod novim ID-om
      if (joinedData && joinedData.length > 0) {
        cacheTableData(id, [...joinedData]); // Koristimo kopiju podataka za novu referencu
      }

      safeSectionLayoutUpdate(() => updatedSectionLayoutState);
      // handleModalType(null, null); // Zatvori modal
    };

    const cleanedLayoutItem = removeTypename(layoutItem);
    const metricsToSave = chartMetrics?.map((metric) => {
      const { __typename, ...rest } = metric;
      return rest;
    });

    if (!layoutItem.SectionItemID) {
      delete cleanedLayoutItem.SectionItemID;
      delete cleanedLayoutItem.Open;
      cleanedLayoutItem.Type = modalType;

      await saveNewSectionItem(sectionID as string, cleanedLayoutItem).then((res) => {
        if (res.status === 200) {
          saveCustomReportQuery(
            res.id as string,
            mainQueryID,
            ccQueryID,
            title,
            isGraph ? metricsToSave : undefined,
            benchmarkQueryID,
            hideTotal,
          ).then((result) => {
            if (result === 'success') {
              updateAndClose(res.id as string);
            } else {
              console.error('Error saving query', result);
            }
          });
        } else {
          alert(res.message);
        }
      });
    } else {
      const queryIDToUse = mainQueryID && mainQueryID !== '' ? mainQueryID : initialQuery?.query_id;

      const ccQueryIDToUse = ccQueryID && ccQueryID !== '' ? ccQueryID : initialQuery?.query_id_cc;
      const benchmarkQueryIDToUse =
        benchmarkQueryID && benchmarkQueryID !== '' ? benchmarkQueryID : initialQuery?.query_id_benchmark;
      saveCustomReportQuery(
        cleanedLayoutItem.SectionItemID as string,
        queryIDToUse as string,
        ccQueryIDToUse as string,
        title,
        isGraph ? metricsToSave : undefined,
        benchmarkQueryIDToUse as string,
        hideTotal,
      ).then((res) => {
        if (res === 'success') {
          updateAndClose(cleanedLayoutItem.SectionItemID as string);
        } else {
          console.error('Error updating query', res);
        }
      });
    }
  };
  const [searchDimension, setSearchDimension] = useState<string>('');
  const [searchMetric, setSearchMetric] = useState<string>('');
  const [searchConversion, setSearchConversion] = useState<string>('');
  useEffect(() => {
    if (selectedDataSource) {
      // setSearchDimension('');
      // setSearchMetric('');
      // setSearchConversion('');
    }
  }, [selectedDataSource]);

  const style = {
    margin: '0',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
  const handleKPIDisplay = (property: string) => {
    if (!joinedData || joinedData.length === 0) {
      return property === 'value' ? 0 : '';
    }

    // Identify dimension columns dynamically from the query
    const dimensionColumns =
      userMainQuery?.query.detailedDimensions?.map((d) => d?.column).filter(Boolean) ?? [];

    // Determine the active row
    let activeRow: any | undefined;
    if (joinedData.length === 1) {
      activeRow = joinedData[0];
    } else if (isKPISumSelected) {
      // SUM mode: should not reach here since query returns 1 row, but guard just in case
      activeRow = joinedData[0];
    } else if (selectedKPIRow) {
      activeRow = joinedData.find((row: any) =>
        selectedKPIRow.every((f) => row[f.column] === f.value),
      );
    }

    if (!activeRow) {
      return property === 'value' ? 0 : '';
    }

    // Find the first numeric key that is not a dimension column
    const metricKey = Object.keys(activeRow).find(
      (k) => !dimensionColumns.includes(k) && typeof activeRow[k] === 'number',
    );

    if (!metricKey) {
      return property === 'value' ? 0 : '';
    }

    if (property === 'key') {
      // Use the draft name (what the user typed), fall back to stored name, then column name
      const detailedMetric = userMainQuery?.query.metrics?.find((m) => m?.column === metricKey);
      return kpiNameDraft || detailedMetric?.name || metricKey;
    } else if (property === 'value') {
      return activeRow[metricKey] ?? 0;
    }
    return '';
  };
  // Prikaz loadera kad nema podataka
  // if ((!dataSources || dataSources.length === 0) && !test) {
  //   return (
  //     <TxtContainer style={style as React.CSSProperties}>
  //       <ReactLoading type="spinningBubbles" color={theme.publishButton} />
  //     </TxtContainer>
  //   );
  // }
  console.log('ALL DATA', {
    joinedData,
    mainData,
    mainTotalData,
    ccData,
    ccTotalData,
    benchmarkData,
    benchmarkTotalData,
  });
  // // Prikaz loadera tijekom dohvaćanja podataka
  if (isLoading) {
    return (
      <TxtContainer style={style as React.CSSProperties}>
        <ReactLoading type="spinningBubbles" color={theme.publishButton} />
      </TxtContainer>
    );
  }

  // Glavni return blok s uvjetnim renderiranjem

  return (
    <>
      {modal ? (
        <OverwriteMainContainer>
          {/* KLJUČNA LOGIKA: Koristimo newQuery za određivanje što prikazati */}
          {newQuery === 'NEW' || newQuery === 'BACK' || !joinedData ? (
            // Prikaz za odabir platforme, dimenzija i metrika
            <>
              <SingleColumn>
                <h1 className="header">Platform</h1>

                {dataSources
                  .filter((el) => el.name !== 'Custom')
                  .map((source) => (
                    <StyledLabel key={source.name}>
                      <input
                        type="checkbox"
                        name="dataSource"
                        value={source.name}
                        checked={selectedPlatforms.includes(source.name)}
                        onChange={(event) => selectSource(event)}
                        id={source.name}
                      />
                      <div className="checked">
                        <span>{source.name}</span>
                      </div>
                    </StyledLabel>
                  ))}
              </SingleColumn>

              <SingleColumn>
                <h1 className="header">Dimensions</h1>
                {selectedDataSource && (
                  <div>
                    <input
                      type="search"
                      placeholder="Search dimensions"
                      value={searchDimension}
                      onChange={(e) => setSearchDimension(e.target.value)}
                    />
                  </div>
                )}
                {selectedDataSource && (
                  <div className="elementsContainer">
                    {selectedDataSource.dimensions
                      ?.filter((d) => d.dimension?.toLowerCase().includes(searchDimension.toLowerCase()))
                      ?.filter(() => true)
                      ?.sort((a, b) => (a.dimension ?? '').localeCompare(b.dimension ?? ''))
                      ?.map((dim) => (
                        <StyledLabel key={dim.dimension}>
                          <input
                            type="checkbox"
                            name={dim.dimension}
                            value={dim.dimension}
                            id={dim.dimension}
                            checked={dim.isSelected || false}
                            onChange={(event) => selectDimension(event)}
                          />
                          <div className="checked">
                            <span>{dim.dimension}</span>
                          </div>
                        </StyledLabel>
                      ))}
                  </div>
                )}
              </SingleColumn>

              <SingleColumn>
                <h1 className="header">Metrics</h1>
                {selectedDataSource && (
                  <div>
                    <input
                      type="search"
                      placeholder="Search metrics"
                      value={searchMetric}
                      onChange={(e) => setSearchMetric(e.target.value)}
                    />
                  </div>
                )}
                {selectedDataSource && (
                  <div className="elementsContainer">
                    {selectedDataSource.metrics
                      ?.filter((m) => m.metric?.toLowerCase().includes(searchMetric.toLowerCase()))
                      ?.sort((a, b) => (a.metric ?? '').localeCompare(b.metric ?? ''))
                      ?.map((metric) => (
                        <StyledLabel key={metric.metric}>
                          <input
                            type="checkbox"
                            name={metric.metric}
                            value={metric.metric}
                            id={metric.metric}
                            checked={metric.isSelected || false}
                            onChange={(event) => selectMetric(event)}
                          />
                          <div className="checked">
                            <span>{metric.metric}</span>
                          </div>
                        </StyledLabel>
                      ))}
                    {selectedDataSource.calcMetrics
                      ?.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
                      ?.map((metric) => (
                        <StyledLabel key={metric.name}>
                          <input
                            type="checkbox"
                            name={metric.name}
                            value={metric.query}
                            id={metric.name}
                            checked={metric.isSelected || false}
                            onChange={(event) => selectCalcMetric(event)}
                          />
                          <div className="checked">
                            <span>{metric.name}</span>
                          </div>
                        </StyledLabel>
                      ))}
                  </div>
                )}
              </SingleColumn>

              <SingleColumn>
                <h1 className="header">Conversions</h1>
                {selectedDataSource && (
                  <div>
                    <input
                      type="search"
                      placeholder="Search conversions"
                      value={searchConversion}
                      onChange={(e) => setSearchConversion(e.target.value)}
                    />
                  </div>
                )}
                {selectedDataSource && selectedDataSource.customConversions && (
                  <div className="elementsContainer">
                    {selectedDataSource.customConversions
                      .filter((conversion) => conversion.CCname?.toLowerCase().includes(searchConversion.toLowerCase()))
                      .sort((a, b) => (a.CCname ?? '').localeCompare(b.CCname ?? ''))
                      .map((conversion) => (
                        <StyledLabel key={conversion.CCname}>
                          <input
                            type="checkbox"
                            name={conversion.CCname}
                            value={conversion.CCname}
                            id={conversion.CCname}
                            checked={conversion.isSelected || false}
                            onChange={(event) => selectCustomConversion(event)}
                          />
                          <div className="checked">
                            <span>{conversion.CCname}</span>
                          </div>
                        </StyledLabel>
                      ))}
                  </div>
                )}
              </SingleColumn>

              <DetailsPane>
                <div className="iconHolder">{platformLogoSelector(selectedDataSource?.name || '')}</div>
                <h1 className="header">Query Overview</h1>
                <div className="details">
                  <div className="p">
                    <h1>Platform:</h1>
                    <span>{selectedDataSource?.name || '-'}</span>
                  </div>
                  <div className="p">
                    <h1>Dimensions:</h1>
                    <span>
                      {selectedDataSource?.dimensions
                        ?.filter((d) => d.isSelected)
                        .map((d) => d.dimension)
                        .join(', ') || '-'}
                    </span>
                  </div>
                  <div className="p">
                    <h1>Metrics:</h1>
                    <span>
                      {selectedDataSource?.metrics
                        ?.filter((m) => m.isSelected)
                        .map((m) => m.metric)
                        .join(', ') || '-'}
                    </span>
                  </div>
                  {selectedDataSource?.calcMetrics?.some((m) => m.isSelected) && (
                    <div className="p">
                      <h1>Calculated Metrics:</h1>
                      <span>
                        {selectedDataSource?.calcMetrics
                          ?.filter((m) => m.isSelected)
                          .map((m) => m.name)
                          .join(', ') || '-'}
                      </span>
                    </div>
                  )}
                  {selectedDataSource?.customConversions?.some((c) => c.isSelected) && (
                    <div className="p">
                      <h1>Custom Conversions:</h1>
                      <span>
                        {selectedDataSource?.customConversions
                          ?.filter((c) => c.isSelected)
                          .map((c) => c.CCname)
                          .join(', ') || '-'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="action-buttons-container-fetch">
                  <button onClick={() => handleModalType(null, null)}>Cancel</button>
                  <DarkButton
                    onClick={prepUserQuery}
                    disabled={
                      !selectedDataSource ||
                      !selectedDataSource.dimensions?.some((d) => d.isSelected) ||
                      !(
                        selectedDataSource.metrics?.some((m) => m.isSelected) ||
                        selectedDataSource.calcMetrics?.some((m) => m.isSelected)
                      )
                    }
                  >
                    Fetch
                  </DarkButton>
                </div>
              </DetailsPane>
            </>
          ) : (
            // Prikaz tablice s rezultatima
            <>
              {modalType === 'kpiQuery' ? (
                // Dedicated KPI results view
                <KPIResultsContainer>
                  <div className="kpi-title-row">
                    <label>KPI Title</label>
                    <input
                      type="text"
                      placeholder="Enter title…"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        setKpiNameDraft(e.target.value);
                      }}
                    />
                  </div>

                  <div className="kpi-preview-box">
                    <span className="kpi-metric-label">{handleKPIDisplay('key') || '—'}</span>
                    {joinedData && joinedData.length > 0 && (selectedKPIRow || joinedData.length === 1) ? (
                      <span className="kpi-metric-value">
                        {Intl.NumberFormat('en-DK', { notation: 'compact' }).format(
                          (handleKPIDisplay('value') as number) ?? 0,
                        )}
                      </span>
                    ) : (
                      <span className="kpi-no-selection">
                        {joinedData && joinedData.length > 1 ? 'Select a row below to preview' : '—'}
                      </span>
                    )}
                  </div>

                  {joinedData && joinedData.length > 1 && (
                    <div className="kpi-row-picker-section">
                      <span className="kpi-row-picker-label">
                        Multiple rows returned — select one to use as your KPI:
                      </span>
                      <div className="kpi-row-picker-list">
                        {/* SUM option — always at the top */}
                        <KPIRowPickerItem
                          selected={isKPISumSelected}
                          onClick={() => setSelectedKPIRow(KPI_SUM_SENTINEL)}
                        >
                          <span className="row-dimension-value" style={{ fontStyle: 'italic' }}>∑ Sum of all rows</span>
                          <div className="row-radio" />
                        </KPIRowPickerItem>
                        {joinedData.map((row: any, idx: number) => {
                          const dimensionColumns =
                            userMainQuery?.query.detailedDimensions?.map((d) => d?.column).filter(Boolean) as string[] ?? [];
                          const metricKey = Object.keys(row).find(
                            (k) => !dimensionColumns.includes(k) && typeof row[k] === 'number',
                          );
                          const metricValue = metricKey ? row[metricKey] : undefined;
                          // All dimension key/value pairs for this row
                          const rowFilters = dimensionColumns
                            .filter((col) => row[col] !== undefined)
                            .map((col) => ({ column: col, value: row[col] }));
                          const isSelected =
                            !isKPISumSelected &&
                            selectedKPIRow !== null &&
                            rowFilters.length === selectedKPIRow.length &&
                            rowFilters.every((f, i) => f.column === selectedKPIRow[i]?.column && f.value === selectedKPIRow[i]?.value);
                          return (
                            <KPIRowPickerItem
                              key={idx}
                              selected={isSelected}
                              onClick={() => setSelectedKPIRow(rowFilters)}
                            >
                              <span className="row-dimension-value">
                                {rowFilters.map((f) => `${f.column}: ${f.value}`).join(' | ')}
                              </span>
                              {metricValue !== undefined && (
                                <span className="row-metric-value">
                                  {Intl.NumberFormat('en-DK', { notation: 'compact' }).format(metricValue)}
                                </span>
                              )}
                              <div className="row-radio" />
                            </KPIRowPickerItem>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="kpi-action-buttons">
                    <button onClick={() => { setNewQuery('BACK'); setSelectedKPIRow(null); }}>Back</button>
                    {joinedData && joinedData.length > 1 ? (
                      // APPLY: bake filter into query and re-run → will reduce to 1 row
                      <DarkButton
                        disabled={!selectedKPIRow}
                        onClick={() => {
                          if (!selectedKPIRow || !userMainQuery) return;
                          const updatedQuery = {
                            ...userMainQuery,
                            query: {
                              ...userMainQuery.query,
                              // Inject the draft metric name into the query definition
                              metrics: userMainQuery.query.metrics?.map((m, i) =>
                                i === 0 && kpiNameDraft ? { ...m!, name: kpiNameDraft } : m,
                              ),
                              ...(isKPISumSelected
                                ? {
                                    // SUM mode: remove all dimensions so backend aggregates everything
                                    detailedDimensions: [],
                                    // Keep only metric/calcMetric IDs in orderBy, drop dimension IDs
                                    orderBy: userMainQuery.query.orderBy?.filter((id) =>
                                      userMainQuery.query.metrics?.some((m) => m?.id === id) ||
                                      userMainQuery.query.calculatedMetrics?.some((cm) => cm?.id === id),
                                    ),
                                    // Strip any dimension EQUAL filters added from previous selections
                                    filters: userMainQuery.query.filters?.filter(
                                      (f) => f?.column === 'campaign_id' || f?.column === 'date' || f?.column === 'client_code',
                                    ),
                                  }
                                : {
                                    // Specific row mode: add one EQUAL filter per selected dimension
                                    filters: [
                                      ...(userMainQuery.query.filters?.filter(
                                        (f) => !selectedKPIRow.some((r) => r.column === f?.column),
                                      ) ?? []),
                                      ...selectedKPIRow.map((r) => ({
                                        column: r.column,
                                        filter: {
                                          filtertype: 'EQUAL' as any,
                                          datatype: 'STRING' as any,
                                          value: String(r.value),
                                        },
                                      })),
                                    ],
                                  }),
                            },
                          };
                          setUserMainQuery(updatedQuery);
                          setNewQuery('APPLY');
                        }}
                      >
                        Apply
                      </DarkButton>
                    ) : (
                      // SAVE: only shown when exactly 1 row
                      <DarkButton onClick={handleSave}>Save</DarkButton>
                    )}
                  </div>
                </KPIResultsContainer>
              ) : (
              <PreviewContainer
                joinedData={joinedData}
                isGraph={isGraph}
                title={title}
                setTitle={setTitle}
                page={page}
                ccData={ccData}
                userMainQuery={userMainQuery}
                userCCQuery={userCCQuery}
                nextPage={nextPage}
                previousPage={previousPage}
                chartMetrics={chartMetrics}
                setChartMetrics={setChartMetrics}
                componentID={element?.id as string}
                element={element}
                mainQueryID={mainQueryID && mainQueryID !== '' ? mainQueryID : (initialQuery?.query_id as string)}
                ccQueryID={ccQueryID && ccQueryID !== '' ? ccQueryID : (initialQuery?.query_id_cc as string)}
                queryError={''}
                setUserMainQuery={(q: any) => setUserMainQuery(q)}
                setUserCCQuery={(q: any) => setUserCCQuery(q)}
                setUserCCTotalQuery={(q: any) => setUserCCTotalQuery(q)}
                setUserMainQueryTotal={(q: any) => setUserMainQueryTotal(q)}
                setPage={setPage}
                userBenchmarkQuery={userBenchmarkQuery}
                setUserBenchmarkQuery={handleBenchmarkQueryUpdate}
                setUserBenchmarkTotalQuery={setUserBenchmarkTotalQuery}
                benchmarkIndex={benchmarkIndex}
                setBenchmarkIndex={setBenchmarkIndex}
                selectedDataSource={selectedDataSource}
                setNewQuery={(newQuery: 'OLD' | 'NEW' | 'BACK' | 'APPLY') => setNewQuery(newQuery)}
                saveCustomReport={() => handleSave()}
                modalType={modalType}
                hideTotal={hideTotal}
                setHideTotal={setHideTotal}
              />
              )}
            </>
          )}
        </OverwriteMainContainer>
      ) : (
        // Prikaz izvan modala
        <>
          <OverwriteMainContainer style={{ height: '100%' }}>
            {userIsAuthorized && (
              <div className="edit-btns">
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalType && handleModalType(layoutItem, modalType);
                  }}
                  className="edit-btn icon material-symbols-outlined"
                >
                  edit
                </div>
                <div
                  className="edit-btn icon material-symbols-outlined"
                  onClick={() => {
                    clearTableDataCache(cacheID);
                    setNewQuery('APPLY');
                  }}
                >
                  refresh
                </div>
                <div className="edit-btn icon material-symbols-outlined" onClick={() => setShowDeleteModal(true)}>
                  delete
                </div>
              </div>
            )}
            {joinedData && joinedData.length > 0 ? (
              // Prikazujemo tablicu/graf ako imamo podatke
              <>
                {isGraph ? (
                  <>
                    {modalType === 'kpiQuery' ? (
                      <CustomReportKPIStyled>
                        <h1 className="title">{handleKPIDisplay('key')}</h1>
                        <h2 className="subtitle" style={{ color: 'transparent' }}>
                          .
                        </h2>
                        <div className="value">
                          {Intl.NumberFormat('en-DK', { notation: 'compact' }).format(handleKPIDisplay('value') ?? 0)}
                        </div>
                      </CustomReportKPIStyled>
                    ) : (
                      <ErrorBoundary fallback={<div>Something went wrong</div>}>
                        <CustomReportResponsiveGridChart
                          joinedData={joinedData}
                          chartMetrics={chartMetrics}
                          layoutItem={layoutItem}
                          title={title || (initialQuery?.title as string)}
                        ></CustomReportResponsiveGridChart>
                      </ErrorBoundary>
                    )}
                  </>
                ) : (
                  <CustomReportTable
                    joinedData={joinedData}
                    title={title || (initialQuery?.title as string)}
                  ></CustomReportTable>
                )}
              </>
            ) : (
              // Nema podataka ili se učitava
              <TxtContainer style={style as React.CSSProperties}>
                {!isLoading && joinedData && joinedData.length === 0 ? (
                  <span
                    style={{
                      color: theme.c300,
                      fontSize: '14px',
                      textAlign: 'center',
                      lineHeight: '20px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    No data available.
                    <br /> Please adjust your query settings and try again.
                  </span>
                ) : (
                  <ReactLoading type="spinningBubbles" color={theme.publishButton} />
                )}
              </TxtContainer>
            )}
          </OverwriteMainContainer>
        </>
      )}

      {showDeleteModal &&
        createPortal(
          <DeleteConfirmationModal
            handleDeleteConfirmationModal={(confirmed) => {
              if (confirmed) {
                deleteItem && deleteItem(sectionID as string, layoutItem.SectionItemID as string);
              }
              setShowDeleteModal(false);
            }}
            modalObject={{
              title: `Delete ${isGraph ? 'Chart' : 'Table'} Element`,
              message: `Are you sure you want to delete this ${isGraph ? 'chart' : 'table'} element? This action cannot be undone.`,
              mainButton: 'Delete',
              secondaryButton: 'Cancel',
            }}
          />,
          document.body,
        )}
    </>
  );
};

export default CustomReportQuery;
